import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {
    Typography,
    Button,
    TextField,
    FormControlLabel,
    Radio,
    RadioGroup,
    IconButton,
    Select,
    MenuItem,
    InputAdornment,
    FormControl,
} from '@mui/material'
import {Add, Check, Close, Delete, Edit} from '@mui/icons-material'
import {clientApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {Controller, useWatch} from 'react-hook-form'
import {parseError, thousantSeparator} from '../../../../utils/helpers'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {DatePicker as DatePickerAntd} from 'antd'
import {format} from 'date-fns'

// setSkeleton: lets the parent swap this form for its skeleton while the lookups load
export default ({form, updating, setSkeleton}) => {
    const {setMessageBox, branchId} = useGlobalStates()
    const [serviceItems, setServiceItems] = useState([])
    const {RangePicker} = DatePickerAntd
    const [rows, setRows] = useState([])
    const [newRows, setNewRows] = useState([])
    const [editingRows, setEditingRows] = useState({})
    const formValues = useWatch({control: form.control})

    const statuses = [
        {key: 'active', value: 'Active'},
        {key: 'inactive', value: 'Inactive'},
    ]

    useEffect(() => {
        ;(async () => {
            if (branchId) {
                try {
                    setSkeleton?.(true)

                    const {
                        data: {data: data1},
                    } = await clientApi.getPayers()
                    if (data1 && data1.length > 0) {
                        form.setValue('payer_id', data1[0]?.id)
                    }

                    const {
                        data: {data: data2},
                    } = await clientApi.getBranchServices({branch_id: branchId})
                    setServiceItems(data2)

                    setSkeleton?.(false)
                } catch (error) {
                    setSkeleton?.(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                }
            }
        })()
    }, [branchId])

    useEffect(() => {
        if (formValues?.service_items) {
            const newVal = formValues?.service_items?.map((item, index) => ({
                ...item,
                row_id: index,
            }))
            if (JSON.stringify(newVal) != JSON.stringify(rows)) {
                setRows(newVal)
            }
        }
    }, [formValues])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (rows) {
                const newVal = rows?.map(item => item)
                if (JSON.stringify(newVal) != JSON.stringify(form.getValues('service_items'))) {
                    form.setValue('service_items', newVal)
                }
            }
        }, 100)
        return () => clearTimeout(timeout)
    }, [rows])

    const addNewRow = () => {
        const new_row_id = Date.now()
        setNewRows([
            ...newRows,
            {new_row_id, id: '', branch_service_id: '', start_date: '', end_date: '', total_units: '', rate_per_unit: '', status: '', unique_id: ''},
        ])
    }

    const newRowChange = (new_row_id, field, value) => {
        setNewRows(prev => prev.map(r => (r.new_row_id === new_row_id ? {...r, [field]: value} : r)))
    }

    const saveNewRow = new_row_id => {
        const rowToSave = newRows.find(r => r.new_row_id === new_row_id)
        if (!rowToSave || !rowToSave.branch_service_id) return

        const unique_id = `${rowToSave?.branch_service_id}|${format(rowToSave?.start_date, 'yyyyMMdd')}|${format(rowToSave?.end_date, 'yyyyMMdd')}`
        if (rows.some(r => r.unique_id === unique_id)) {
            setMessageBox({status: 'warning', title: 'Duplicate Entry', description: 'Service, start date and end date must unique.'})
            return
        }

        const row_id = rows.length ? Math.max(...rows.map(r => r.row_id)) + 1 : 1
        setRows([...rows, {...rowToSave, row_id, unique_id}])
        setNewRows(newRows.filter(r => r.new_row_id !== new_row_id))
    }

    const cancelNewRow = new_row_id => {
        setNewRows(newRows.filter(r => r.new_row_id !== new_row_id))
    }

    const deleteRow = row_id => {
        setRows(rows.filter(r => r.row_id !== row_id))
        const updated = {...editingRows}
        delete updated[row_id]
        setEditingRows(updated)
    }

    const editRow = row => {
        setEditingRows(prev => ({
            ...prev,
            [row.row_id]: prev[row.row_id] ? undefined : {...row},
        }))
    }

    const editRowChange = (row_id, field, value) => {
        setEditingRows(prev => ({
            ...prev,
            [row_id]: {
                ...prev[row_id],
                [field]: value,
            },
        }))
    }

    const saveEditRow = row_id => {
        if (!editingRows[row_id]?.id) {
            const unique_id = `${editingRows[row_id]?.branch_service_id}|${format(editingRows[row_id]?.start_date, 'yyyyMMdd')}|${format(editingRows[row_id]?.end_date, 'yyyyMMdd')}`
            if (rows.some(r => r.row_id !== row_id && r.unique_id === unique_id)) {
                setMessageBox({status: 'warning', title: 'Duplicate Entry', description: 'Service, start date and end date must unique.'})
                return
            }
        }

        setRows(prev => prev.map(r => (r.row_id === row_id ? editingRows[row_id] : r)))
        const updated = {...editingRows}
        delete updated[row_id]
        setEditingRows(updated)
    }

    const cancelEditRow = row_id => {
        const updated = {...editingRows}
        delete updated[row_id]
        setEditingRows(updated)
    }

    return (
        <>
            <Typography variant="h7" color="primary.dark">
                Document Details
            </Typography>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Service Agreement Number
            </Typography>
            <TextField placeholder="Enter your service agreement number" fullWidth {...form.register('agreement_number')} disabled={updating} />
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Service Agreement Status
            </Typography>
            <FormControl fullWidth>
                <Controller
                    {...form.register(`status`)}
                    control={form.control}
                    render={({field}) => (
                        <Select
                            {...field}
                            value={field.value ?? 'active'}
                            onChange={e => field.onChange(e.target.value)}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                            }}>
                            {statuses.map(item => (
                                <MenuItem key={item?.key} value={item?.key}>
                                    {item?.value}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Diagnosis Code{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <TextField
                placeholder="Enter your diagnosis code"
                fullWidth
                {...form.register('diagnosis_code')}
                error={!!form.formState.errors['diagnosis_code']}
                helperText={form.formState.errors['diagnosis_code']?.message}
            />
            <br />
            <br />
            <div className="form">
                <div className="form-left">
                    <Typography variant="inputLabel" color="text.secondary">
                        Effective Date{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <Controller
                        {...form.register('effective_date')}
                        control={form.control}
                        render={({field}) => (
                            <DatePicker
                                placeholder="Enter your effective date"
                                {...field}
                                value={field.value ?? null}
                                onChange={newValue => field.onChange(newValue)}
                                enableAccessibleFieldDOMStructure={false}
                                slots={{
                                    textField: params => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            error={!!form.formState.errors['effective_date']}
                                            helperText={form.formState.errors['effective_date']?.message}
                                        />
                                    ),
                                }}
                            />
                        )}
                    />
                </div>
                <div className="form-right">
                    <Typography variant="inputLabel" color="text.secondary">
                        Through Date{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <Controller
                        {...form.register('through_date')}
                        control={form.control}
                        render={({field}) => (
                            <DatePicker
                                placeholder="Enter your through date"
                                {...field}
                                value={field.value ?? null}
                                onChange={newValue => field.onChange(newValue)}
                                enableAccessibleFieldDOMStructure={false}
                                slots={{
                                    textField: params => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            error={!!form.formState.errors['through_date']}
                                            helperText={form.formState.errors['through_date']?.message}
                                        />
                                    ),
                                }}
                            />
                        )}
                    />
                </div>
            </div>
            <br />
            <br />
            <Typography variant="h7" color="primary.dark">
                Payor Details
            </Typography>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Payor Name{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <RadioGroup value="a-z">
                <FormControlLabel value="a-z" control={<Radio />} label="Department of Human Services / Medicaid" />
            </RadioGroup>
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                MA/MCO Number{' '}
                <Typography variant="inputLabel" color="error">
                    *
                </Typography>
            </Typography>
            <TextField
                placeholder="Enter your MA/MCO number"
                fullWidth
                {...form.register('ma_mco_number')}
                error={!!form.formState.errors['ma_mco_number']}
                helperText={form.formState.errors['ma_mco_number']?.message}
            />
            <br />
            <br />
            <br />
            <Typography variant="h7" color="primary.dark">
                Services
            </Typography>
            <br />
            {rows.map((row, index) => {
                const isEditing = !!editingRows[row.row_id]
                const editValues = editingRows[row.row_id] || {}
                return (
                    <div key={row.row_id}>
                        {isEditing ? (
                            <>
                                <br />
                                <div className="card">
                                    <div className="card-top">
                                        <Typography variant="h7" color="primary">
                                            {String(index + 1).padStart(2, '0')}
                                        </Typography>
                                        <div className="card-top-actions">
                                            <IconButton color="error" size="small" onClick={() => cancelEditRow(row.row_id)}>
                                                <Close />
                                            </IconButton>
                                            <IconButton color="success" size="small" onClick={() => saveEditRow(row.row_id)}>
                                                <Check />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <br />
                                    <div className="card-service">
                                        <div className="card-service-left">
                                            {row?.id ? (
                                                <>
                                                    <Typography variant="inputLabel" color="text.secondary">
                                                        Service Care
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        {serviceItems?.find(item => item?.id == row?.branch_service_id)?.service_name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" style={{marginTop: -8}}>
                                                        {serviceItems?.find(item => item?.id == row?.branch_service_id)?.hcpc_code}-
                                                        {serviceItems
                                                            ?.find(item => item?.id == row?.branch_service_id)
                                                            ?.billing_unit?.replace('_', ' ')}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="inputLabel" color="text.secondary">
                                                        Service Care
                                                    </Typography>
                                                    <Select
                                                        fullWidth
                                                        value={editValues.branch_service_id}
                                                        onChange={e => editRowChange(row.row_id, 'branch_service_id', e.target.value)}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: 300,
                                                                },
                                                            },
                                                            anchorOrigin: {
                                                                vertical: 'top',
                                                                horizontal: 'left',
                                                            },
                                                            transformOrigin: {
                                                                vertical: 'bottom',
                                                                horizontal: 'left',
                                                            },
                                                        }}>
                                                        {serviceItems?.map(item => (
                                                            <MenuItem value={item?.id}>{item?.service_name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </>
                                            )}
                                        </div>
                                        <div className="card-service-right">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Status
                                            </Typography>
                                            <Select
                                                fullWidth
                                                value={editValues.status}
                                                onChange={e => editRowChange(row.row_id, 'status', e.target.value)}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300,
                                                        },
                                                    },
                                                    anchorOrigin: {
                                                        vertical: 'top',
                                                        horizontal: 'left',
                                                    },
                                                    transformOrigin: {
                                                        vertical: 'bottom',
                                                        horizontal: 'left',
                                                    },
                                                }}>
                                                <MenuItem value="active">Active</MenuItem>
                                                <MenuItem value="inactive">Inactive</MenuItem>
                                            </Select>
                                        </div>
                                    </div>
                                    <br />
                                    <div className="card-date">
                                        <div className="card-date-left">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Start Date - End Date
                                            </Typography>
                                            <RangePicker
                                                value={
                                                    editingRows[row?.row_id]?.start_date && editingRows[row?.row_id]?.end_date
                                                        ? [editingRows[row?.row_id]?.start_date, editingRows[row?.row_id]?.end_date]
                                                        : [null, null]
                                                }
                                                onChange={e => {
                                                    const [start_date, end_date] = e
                                                    editRowChange(row.row_id, 'start_date', start_date)
                                                    editRowChange(row.row_id, 'end_date', end_date)
                                                }}
                                                format="MM/DD/YYYY"
                                                allowClear={false}
                                                style={{
                                                    borderRadius: 16,
                                                    border: '1px solid #0000003B',
                                                    backgroundColor: '#ffffff',
                                                    height: 56,
                                                    color: '#00000099',
                                                    width: '100%',
                                                }}
                                                placement="bottomRight"
                                            />
                                        </div>
                                        <div className="card-date-right">
                                            <div className="content">
                                                <Typography variant="inputLabel" color="text.secondary">
                                                    Total Units
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    value={editValues.total_units}
                                                    onChange={e => editRowChange(row.row_id, 'total_units', e.target.value)}
                                                    type="number"
                                                />
                                            </div>
                                            <div className="content">
                                                <Typography variant="inputLabel" color="text.secondary">
                                                    Rate/Unit
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    value={editValues.rate_per_unit}
                                                    onChange={e => editRowChange(row.row_id, 'rate_per_unit', e.target.value)}
                                                    type="number"
                                                    slotProps={{
                                                        input: {
                                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                        },
                                                    }}
                                                />
                                            </div>
                                            <div className="content" style={{gap: 26, minWidth: 100}}>
                                                <Typography variant="inputLabel" color="text.primary">
                                                    Total Cost
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    ${thousantSeparator((editValues.total_units * editValues.rate_per_unit)?.toFixed(2))}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <br />
                                <div className="card">
                                    <div className="card-top">
                                        <Typography variant="h7" color="primary">
                                            {String(index + 1).padStart(2, '0')}
                                        </Typography>
                                        <div className="card-top-actions">
                                            <IconButton color="primary" size="small" onClick={() => editRow(row)}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton color="error" size="small" onClick={() => deleteRow(row?.row_id)}>
                                                <Delete />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <br />
                                    <div className="card-service">
                                        <div className="card-service-left">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Service Care
                                            </Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {serviceItems?.find(item => item?.id == row?.branch_service_id)?.service_name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" style={{marginTop: -8}}>
                                                {serviceItems?.find(item => item?.id == row?.branch_service_id)?.hcpc_code}-
                                                {serviceItems?.find(item => item?.id == row?.branch_service_id)?.billing_unit?.replace('_', ' ')}
                                            </Typography>
                                        </div>
                                        <div className="card-service-right">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Status
                                            </Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {row?.status?.charAt(0).toUpperCase() + row?.status?.slice(1)}
                                            </Typography>
                                        </div>
                                    </div>
                                    <br />
                                    <div className="card-date">
                                        <div className="card-date-left">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Start Date - End Date
                                            </Typography>
                                            <Typography variant="body2" color="text.primary">
                                                {row?.start_date ? format(new Date(row?.start_date), 'MM/dd/yyyy') : ''} -{' '}
                                                {row?.end_date ? format(new Date(row?.end_date), 'MM/dd/yyyy') : ''}
                                            </Typography>
                                        </div>
                                        <div className="card-date-right">
                                            <div className="content">
                                                <Typography variant="inputLabel" color="text.secondary">
                                                    Total Unit
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {thousantSeparator(row?.total_units)}
                                                </Typography>
                                            </div>
                                            <div className="content">
                                                <Typography variant="inputLabel" color="text.secondary">
                                                    Rate/Unit
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    ${thousantSeparator(row?.rate_per_unit)}
                                                </Typography>
                                            </div>
                                            <div className="content">
                                                <Typography variant="inputLabel" color="text.secondary">
                                                    Total Cost
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    ${thousantSeparator((row?.total_units * row?.rate_per_unit)?.toFixed(2))}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )
            })}
            {newRows.map((row, index) => (
                <div key={row.new_row_id}>
                    <br />
                    <div className="card">
                        <div className="card-top">
                            <Typography variant="h7" color="primary">
                                {String(rows.length + index + 1).padStart(2, '0')}
                            </Typography>
                            <div className="card-top-actions">
                                <IconButton color="error" size="small" onClick={() => cancelNewRow(row.new_row_id)}>
                                    <Close />
                                </IconButton>
                                <IconButton color="success" size="small" onClick={() => saveNewRow(row.new_row_id)}>
                                    <Check />
                                </IconButton>
                            </div>
                        </div>
                        <br />
                        <div className="card-service">
                            <div className="card-service-left">
                                <Typography variant="inputLabel" color="text.secondary">
                                    Service Care
                                </Typography>
                                <Select
                                    fullWidth
                                    value={row.branch_service_id}
                                    onChange={e => newRowChange(row.new_row_id, 'branch_service_id', e.target.value)}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                        anchorOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                    }}>
                                    {serviceItems?.map(item => (
                                        <MenuItem value={item?.id}>{item?.service_name}</MenuItem>
                                    ))}
                                </Select>
                            </div>
                            <div className="card-service-right">
                                <Typography variant="inputLabel" color="text.secondary">
                                    Status
                                </Typography>
                                <Select
                                    fullWidth
                                    value={row.status}
                                    onChange={e => newRowChange(row.new_row_id, 'status', e.target.value)}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                        anchorOrigin: {
                                            vertical: 'top',
                                            horizontal: 'left',
                                        },
                                        transformOrigin: {
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        },
                                    }}>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </div>
                        </div>
                        <br />
                        <div className="card-date">
                            <div className="card-date-left">
                                <Typography variant="inputLabel" color="text.secondary">
                                    Start Date - End Date
                                </Typography>
                                <RangePicker
                                    value={row?.start_date && row?.end_date ? [row?.start_date, row?.end_date] : [null, null]}
                                    onChange={e => {
                                        const [start_date, end_date] = e
                                        newRowChange(row.new_row_id, 'start_date', start_date)
                                        newRowChange(row.new_row_id, 'end_date', end_date)
                                    }}
                                    format="MM/DD/YYYY"
                                    allowClear={false}
                                    style={{
                                        borderRadius: 16,
                                        border: '1px solid #0000003B',
                                        backgroundColor: '#ffffff',
                                        height: 56,
                                        color: '#00000099',
                                        width: '100%',
                                    }}
                                    placement="bottomRight"
                                />
                            </div>
                            <div className="card-date-right">
                                <div className="content">
                                    <Typography variant="inputLabel" color="text.secondary">
                                        Total Units
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value={row.total_units}
                                        onChange={e => newRowChange(row.new_row_id, 'total_units', e.target.value)}
                                        type="number"
                                    />
                                </div>
                                <div className="content">
                                    <Typography variant="inputLabel" color="text.secondary">
                                        Rate/Unit
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        value={row.rate_per_unit}
                                        onChange={e => newRowChange(row.new_row_id, 'rate_per_unit', e.target.value)}
                                        type="number"
                                        slotProps={{
                                            input: {
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                            },
                                        }}
                                    />
                                </div>
                                <div className="content" style={{gap: 26, minWidth: 100}}>
                                    <Typography variant="inputLabel" color="text.secondary">
                                        Total Cost
                                    </Typography>
                                    <Typography variant="body2" color="text.primary">
                                        ${thousantSeparator((row.total_units * row.rate_per_unit)?.toFixed(2))}
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            <br />
            <br />
            <Button startIcon={<Add />} variant="outlined" onClick={addNewRow}>
                Add more service
            </Button>
        </>
    )
}
